import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";

import Api from "../../../../utils/Api";

export default function ChoixBadgePopup({
  isOpen,
  onClose,
  selectedBadges,
  setSelectedBadges,
}) {
  const [isLoadingBadges, setIsLoadingBadges] = useState(false);
  const [badgesLoaded, setBadgesLoaded] = useState([]);
  const [search, setSearch] = useState("");
  const [scroll, setScroll] = useState("paper");

  useEffect(() => {
    if (!isOpen) return;

    setScroll("paper");
    refreshBadges();
  }, [isOpen]);

  /**
   * Trouve tous les badges qui peuvent être assignés aux utilisateurs
   */
  const refreshBadges = () => {
    setIsLoadingBadges(true);

    Api.get("/badge/all-active")
      .then((response) => {
        setBadgesLoaded(response.data.badges);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoadingBadges(false);
      });
  };

  /**
   * Ferme la fenêtre et réinitialise les valeurs
   */
  const handleCancel = () => {
    setSelectedBadges([]);
    onClose();
  };

  const handleSubmit = () => {
    onClose();
  };

  /**
   * Fonction qui permet de mettre à jour la valeur du search
   * @param {*} event
   */
  function handleChange(event) {
    const valeur = event.target.value;
    setSearch(valeur);
  }

  /**
   * Fonction qui permet de filtrer les badges selon le nom entré dans le search
   *
   * @author: Félix Dupras-Simard [felixdupras@gmail.com]
   */
  const badgesFiltreNom = badgesLoaded.filter((badge) => {
    const searchTerme = search.toLowerCase();
    return badge.title.toLowerCase().startsWith(searchTerme);
  });

  /**
   * Sélectionner ou désélectionner un badge pour l'assignation
   *
   * @author: Félix Dupras-Simard [felixdupras@gmail.com]
   * D'après le code de projet-ebadges
   * Inspiré de ClaudeAI - [Modèle massif de langage] - claude.ai - [Consulté le 19 mars 2026]
   */
  const selectionBadge = (badge) => {
    setSelectedBadges((prev) =>
      prev.some((b) => b.id === badge.id)
        ? prev.filter((b) => b.id !== badge.id)
        : [...prev, badge],
    );
  };

  /*
   * Autocomplétion trié en ordre alphabétiquepour rechercher un utilisateur dans le classement
   *
   * @author: Félix Dupras-Simard [felixdupras@gmail.com]
   * Inspiré de MUI - [Modèle massif de langage] - https://mui.com/material-ui/react-autocomplete/#combo-box - [Consulté le 9 avril 2026]
   * Inspiré de StackOverflow - https://stackoverflow.com/questions/73582640/groupby-autocomplete-sorting-of-options-in-material-ui#73582994
   */
  function AutocompletionRechercheTri() {
    const options = [...badgesLoaded]
      .sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1))
      .map((badge) => `${badge.title}`);
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "left",
          mb: 2,
        }}
      >
        <Autocomplete
          id="rechercheLeaderboard"
          freeSolo
          options={options}
          sx={{ width: 300 }}
          inputValue={search}
          onInputChange={(event, newInputValue) => setSearch(newInputValue)}
          renderInput={(params) => (
            <TextField {...params} label="Rechercher un utilisateur" />
          )}
        />
      </Box>
    );
  }

  /**
   * Visuel pour l'affichage des badges disponibles à l'assignation
   *
   * @author: Félix Dupras-Simard [felixdupras@gmail.com]
   * D'après le code de projet-ebadge
   * Inspiré de ClaudeAI - [Modèle massif de langage] - claude.ai - [Consulté le 17 mars 2026]
   */
  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      scroll={scroll}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Choix des badges à assigner</DialogTitle>
      <DialogContent dividers={scroll === "paper"}>
        {isLoadingBadges ? (
          <p>Chargement des badges...</p>
        ) : badgesLoaded.length > 0 ? (
          badgesLoaded.length > 0 && (
            <>
              {AutocompletionRechercheTri()}
              <Box>
                <Typography
                  variant="body1"
                  color="text.primary"
                  fontWeight={500}
                  mt={2}
                >
                  Nombre Sélectionné : {selectedBadges.length}
                </Typography>
              </Box>
              <Box></Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: "14px",
                  mt: 1,
                }}
              >
                {badgesFiltreNom.map((badge) => {
                  const isSelected = selectedBadges.some(
                    (b) => b.id === badge.id,
                  );

                  return (
                    <Card
                      key={badge.id}
                      elevation={0}
                      onClick={() => {
                        selectionBadge(badge);
                      }}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "10px",
                        p: "18px 12px 14px",
                        borderRadius: 3,
                        cursor: "pointer",
                        transition:
                          "border-color 0.15s, transform 0.15s, background-color 0.15s",
                        border: "2px solid",
                        borderColor: isSelected ? "primary.main" : "divider",
                        backgroundColor: isSelected
                          ? "primary.light"
                          : "background.paper",
                        "&:hover": {
                          borderColor: isSelected
                            ? "primary.dark"
                            : "text.primary",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Avatar
                        src={badge.imagePath}
                        alt={badge.title}
                        sx={{
                          width: 62,
                          height: 62,
                          boxShadow: `0 0 0 3px ${badge.category_color}55, 0 0 0 6px ${badge.category_color}22`,
                        }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        textAlign="center"
                        lineHeight={1.3}
                      >
                        {badge.title}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: badge.category_color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {badge.category}
                        </Typography>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            </>
          )
        ) : (
          <p>Aucun badge disponible</p>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit}>Soumettre</Button>
        <Button onClick={handleCancel}>Annuler</Button>
      </DialogActions>
    </Dialog>
  );
}
